import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { handleApiResponse, getAuthHeaders, setToken } from '../utils/auth';
import UpdatePhone from './UpdatePhone';

const API_BASE = import.meta.env.VITE_API_BASE;

// Country options with flags
const COUNTRIES = [
  { name: 'United States', code: 'US', dial: '1' },
  { name: 'United Kingdom', code: 'GB', dial: '44' },
  { name: 'Israel', code: 'IL', dial: '972' },
  { name: 'Germany', code: 'DE', dial: '49' },
  { name: 'France', code: 'FR', dial: '33' },
  { name: 'Japan', code: 'JP', dial: '81' }
];

const countryOptions = COUNTRIES.map(c => ({
  value: c.dial,
  label: (
    <div className="flex items-center">
      <img
        src={`https://flagcdn.com/16x12/${c.code.toLowerCase()}.png`}
        alt={c.name}
        className="w-4 h-3 mr-2"
      />
      <span className="text-sm">+{c.dial}</span>
    </div>
  )
}));

const selectStyles = {
  control: (base) => ({
    ...base,
    height: '40px',
    minHeight: '40px',
    backgroundColor: 'var(--card-bg)',
    borderColor: 'var(--border-color)',
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: 'var(--card-bg)',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? 'var(--hover-light)' : 'transparent',
    ':active': {
      backgroundColor: 'var(--hover-dark)',
    },
  }),
  singleValue: (base) => ({
    ...base,
    color: 'var(--text-primary)',
  }),
  indicatorSeparator: () => ({
    display: 'none'
  }),
};

const VerificationCodeInput = ({ onCodeComplete }) => {
  const [code, setCode] = useState(['', '', '', '', '']);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const inputRefs = useRef([]);

  // Auto-focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // Handle global keyboard input
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Prevent handling if the target is one of our inputs
      if (e.target.matches('input[type="text"]')) {
        return;
      }

      // Only handle numeric keys and backspace
      if ((/^\d$/.test(e.key) || e.key === 'Backspace') && !e.ctrlKey && !e.altKey && !e.metaKey) {
        if (e.key === 'Backspace') {
          // Handle backspace
          const newCode = [...code];
          if (newCode[focusedIndex] === '') {
            // If current position is empty, move back and clear previous
            if (focusedIndex > 0) {
              newCode[focusedIndex - 1] = '';
              setCode(newCode);
              setFocusedIndex(focusedIndex - 1);
              inputRefs.current[focusedIndex - 1]?.focus();
            }
          } else {
            // Clear current position
            newCode[focusedIndex] = '';
            setCode(newCode);
          }
          onCodeComplete('');
        } else {
          // Handle numeric input
          const newCode = [...code];
          newCode[focusedIndex] = e.key;
          setCode(newCode);
          
          if (focusedIndex < 4) {
            setFocusedIndex(focusedIndex + 1);
            inputRefs.current[focusedIndex + 1]?.focus();
          }

          // Check if all digits are filled
          if (newCode.every(digit => digit !== '')) {
            onCodeComplete(newCode.join(''));
          } else {
            onCodeComplete('');
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [code, focusedIndex, onCodeComplete]);

  const handleInput = (e, index) => {
    const value = e.target.value.slice(-1);
    if (/^\d$/.test(value)) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);
      
      if (index < 4) {
        setFocusedIndex(index + 1);
        inputRefs.current[index + 1]?.focus();
      }

      // Check if all digits are filled
      if (newCode.every(digit => digit !== '')) {
        onCodeComplete(newCode.join(''));
      } else {
        onCodeComplete('');
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const prevIndex = Math.max(0, index - 1);
      setFocusedIndex(prevIndex);
      inputRefs.current[prevIndex]?.focus();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      const nextIndex = Math.min(4, index + 1);
      setFocusedIndex(nextIndex);
      inputRefs.current[nextIndex]?.focus();
    } else if (e.key === 'Backspace') {
      e.preventDefault();
      const newCode = [...code];
      if (newCode[index] === '' && index > 0) {
        newCode[index - 1] = '';
        setFocusedIndex(index - 1);
        inputRefs.current[index - 1]?.focus();
      } else {
        newCode[index] = '';
      }
      setCode(newCode);
      onCodeComplete('');
    }
  };

  const handleFocus = (index) => {
    setFocusedIndex(index);
    inputRefs.current[index]?.select();
  };

  return (
    <div className="flex justify-center gap-4">
      {code.map((digit, index) => (
        <input
          key={index}
          ref={el => inputRefs.current[index] = el}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={digit}
          onChange={(e) => handleInput(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onFocus={() => handleFocus(index)}
          onClick={() => handleFocus(index)}
          className={`w-12 h-12 text-center border rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] caret-transparent ${
            focusedIndex === index 
              ? 'bg-[var(--input-bg-focus)] border-[var(--primary-color)]' 
              : 'bg-[var(--input-bg)] border-[var(--border-color)]'
          } text-[var(--text-primary)]`}
        />
      ))}
    </div>
  );
};

export default function PhoneVerification({ regToken, onRegistrationFailed }) {
  const [isVerified, setIsVerified] = useState(false);
  const [countryPhone, setCountryPhone] = useState('');
  const [phone, setPhone] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isCodeComplete, setIsCodeComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const navigate = useNavigate();
  const initialCheckDone = useRef(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Add an effect to set initial cooldown when component mounts
  useEffect(() => {
    setResendCooldown(8);
    const interval = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (initialCheckDone.current) return;
    initialCheckDone.current = true;
    console.log('PhoneVerification component mounted, calling checkVerification()');
    checkVerification();
  }, [regToken, navigate]);

  const checkVerification = async () => {
    if (checking) return;
    setChecking(true);
    console.log('Starting phone verification check...');
    
    try {
      console.log(`Fetching from: ${API_BASE}/signup/${regToken}/phonev`);
      // First try with auth headers
      let response = await fetch(`${API_BASE}/signup/${regToken}/phonev`, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      // If we get 401, try again without auth headers
      if (response.status === 401) {
        console.log('401 response with auth headers, retrying without auth headers');
        response = await fetch(`${API_BASE}/signup/${regToken}/phonev`, {
          method: 'GET'
        });
      }

      console.log('Response status:', response.status);

      if (response.status === 404) {
        console.log('404 response, calling onRegistrationFailed');
        onRegistrationFailed();
        return;
      }

      if (response.status === 401) {
        // If still 401 after retry, clear local storage and redirect to login
        console.log('Still getting 401, clearing storage and redirecting to login');
        localStorage.clear();
        sessionStorage.removeItem('registrationToken');
        navigate('/login', { replace: true });
        return;
      }

      if (response.status === 406) {
        // Need to collect full name for invite flow
        const searchParams = new URLSearchParams(location.search);
        navigate(`/signup/${regToken}/invite?from=email&invite=true&key=${searchParams.get('key')}`, { replace: true });
        return;
      }

      const data = await response.json();
      console.log('Phone verification status (raw data):', data);

      // Check if phone is missing and redirect to update phone
      if (data.missingPhone === true) {
        console.log('missingPhone is true, preparing to redirect...');
        console.log(`Redirect target: /signup/${regToken}/updatephone`);
        // Use navigate with state to prevent loops
        navigate(`/signup/${regToken}/updatephone`, { 
          replace: true,
          state: { fromPhoneVerification: true }
        });
        return;
      }

      // Handle redirect URL if present
      if (data.redirectUrl) {
        console.log(`redirectUrl present: ${data.redirectUrl}, preparing to navigate...`);
        if (data.redirectUrl === '/login') {
          sessionStorage.removeItem('registrationToken');
        }
        navigate(data.redirectUrl, { replace: true });
        return;
      }

      if (!response.ok) {
        console.log('Response not OK, throwing error');
        throw new Error(data.message || 'Failed to verify phone');
      }

      // Set phone number from API response if available
      if (data.countryPhone && data.phone) {
        console.log(`Setting phone: +${data.countryPhone} ${data.phone}`);
        setCountryPhone(data.countryPhone);
        setPhone(data.phone);
      }

      console.log(`Setting isVerified to: ${data.verified}`);
      setIsVerified(data.verified);
    } catch (err) {
      console.error('Failed to check phone verification:', err);
      setError('Failed to verify phone. Please try again.');
    } finally {
      setChecking(false);
      console.log('Verification check completed');
    }
  };

  const handleResendCode = async () => {
    if (loading || resendCooldown > 0) return;
    
    try {
      setLoading(true);
      setResendCooldown(8); // Start 8-second cooldown
      const interval = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // First try with auth headers
      let res = await fetch(`${API_BASE}/signup/${regToken}/resendsms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          phoneCountry: countryPhone,
          phone: phone
        })
      });

      // If unauthorized, try without auth headers
      if (res.status === 401) {
        console.log('401 response for resend, trying without auth headers');
        res = await fetch(`${API_BASE}/signup/${regToken}/resendsms`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            phoneCountry: countryPhone,
            phone: phone
          })
        });
      }

      if (res.status === 404) {
        onRegistrationFailed();
        return;
      }

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setGeneralError(data.message || 'Failed to resend verification code');
      }

    } catch (err) {
      setGeneralError('Failed to resend verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeComplete = (code) => {
    setVerificationCode(code);
    setIsCodeComplete(code.length === 5);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!verificationCode || verificationCode.length !== 5 || submitting) return;

    setSubmitting(true);
    setError('');

    try {
      console.log('Submitting phone verification code...');
      // First try with auth headers
      let response = await fetch(`${API_BASE}/signup/${regToken}/verifyPhone?code=${verificationCode}`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders()
        }
      });

      // If we get 401, try again without auth headers
      if (response.status === 401) {
        console.log('401 response with auth headers, retrying without auth headers');
        response = await fetch(`${API_BASE}/signup/${regToken}/verifyPhone?code=${verificationCode}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        });
      }

      console.log('Phone verification response status:', response.status);

      if (response.status === 406) {
        // Need to collect full name for invite flow
        const searchParams = new URLSearchParams(location.search);
        navigate(`/signup/${regToken}/invite?from=email&invite=true&key=${searchParams.get('key')}`, { replace: true });
        return;
      }

      const data = await response.json();
      console.log('Phone verification response data:', data);

      if (!response.ok) {
        throw new Error(data.message || 'Failed to verify code');
      }

      // Handle JWT token if present in any form
      const token = data.jwtToken || data.token || data.jwt;
      console.log('Token present in response:', !!token);
      
      if (token) {
        console.log('Setting JWT token and cleaning up registration...');
        // Save token in both localStorage and using setToken utility
        localStorage.setItem('jwt', token);
        setToken(token);
        sessionStorage.removeItem('registrationToken');
        navigate('/home', { replace: true });
        return;
      }

      // If no token but redirectUrl present
      if (data.redirectUrl) {
        console.log('Redirect URL present:', data.redirectUrl);
        if (data.redirectUrl === '/login') {
          sessionStorage.removeItem('registrationToken');
        } else if (data.redirectUrl === '/home') {
          // Some APIs might send /home without token
          const existingToken = localStorage.getItem('jwt');
          if (existingToken) {
            window.dispatchEvent(new Event('auth-token-changed'));
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
        navigate(data.redirectUrl, { replace: true });
        return;
      }

      console.log('No token or redirect URL in response, setting verified state');
      setIsVerified(true);
    } catch (err) {
      console.error('Failed to verify code:', err);
      setError(err.message || 'Failed to verify code. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWrongNumber = () => {
    navigate(`/signup/${regToken}/updatephone`, {
      state: { currentPhoneCountry: countryPhone }
    });
  };

  return (
    <div className="max-w-sm w-full sm:max-w-md p-4 bg-[var(--card-bg)] rounded-lg border border-[var(--border-color)] space-y-2">
      <div className="space-y-6">
        <div className="text-center space-y-2">
          <div className="flex justify-center mb-4">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path 
                d="M17 2H7C5.9 2 5 2.9 5 4V20C5 21.1 5.9 22 7 22H17C18.1 22 19 21.1 19 20V4C19 2.9 18.1 2 17 2ZM17 18H7V6H17V18Z" 
                fill="var(--accent-primary)"
              />
              <path 
                d="M12 19C12.5523 19 13 18.5523 13 18C13 17.4477 12.5523 17 12 17C11.4477 17 11 17.4477 11 18C11 18.5523 11.4477 19 12 19Z" 
                fill="var(--accent-primary)"
              />
            </svg>
          </div>

          <p className="text-[var(--text-primary)] mb-2">
            Enter the verification code sent to
          </p>
          <p className="text-[var(--text-primary)] font-medium">
            {countryPhone && phone ? `+${countryPhone} ${phone}` : ''}
          </p>

          <div className="text-[var(--text-secondary)] text-sm text-center mt-1">
            <button
              onClick={handleWrongNumber}
              className="text-[var(--accent-primary)] hover:text-[var(--text-primary)] hover:underline font-medium"
            >
              Change phone number?
            </button>
          </div>
        </div>

        <VerificationCodeInput onCodeComplete={handleCodeComplete} />

        {error && (
          <p className="text-red-500 text-center text-sm">{error}</p>
        )}

        <div className="space-y-4">
          <button
            onClick={handleSubmit}
            disabled={!isCodeComplete || submitting}
            className={`w-full flex justify-center items-center px-4 py-2 font-semibold rounded transition
              ${isCodeComplete 
                ? 'bg-[var(--accent-primary)] text-[var(--btn-text-on-accent)] hover:bg-[var(--hover-dark)]' 
                : 'bg-[var(--disabled-bg)] text-[var(--text-secondary)]'}`}
          >
            {submitting ? (
              <div className="flex items-center">
                <div className="w-5 h-5 border-2 border-[var(--btn-text-on-accent)] border-t-transparent rounded-full animate-spin mr-2"></div>
                Verifying...
              </div>
            ) : (
              'Continue'
            )}
          </button>

          <div className="text-[var(--text-secondary)] text-sm text-center">
            <p>Didn't receive the code?</p>
            <p className="mt-1">
              Check your messages or{' '}
              {resendCooldown > 0 ? (
                <span className="text-[var(--text-secondary)] opacity-50">
                  Resend SMS
                </span>
              ) : (
                <button
                  onClick={handleResendCode}
                  className="text-[var(--accent-primary)] hover:text-[var(--text-primary)] hover:underline font-medium"
                >
                  Resend SMS
                </button>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
} 